import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { PartyInvolved } from "../../../api/PartiesInvolved";
import { AccountType, AreaOfFraud, CreateCaseRequest } from "../../../api/cases";
import { Account } from "../../../api/accounts";
import {
    CreateCaseStakeholderRequest,
    LoanAccount,
    LOAN_ACCOUNT_ROLES,
    PrimaryParty,
    AccountRelations,
    StakeholderRelationshipFields,
} from "../../../api/creditAccounts";
import { generateCaseUIN } from "../../../lib/utils";
import { CreditAccount, CreditAccountProduct, CreditFacility } from "../../../api/credit_accounts";
import { EntityType, RelatedAs } from "../../../api/stakeholder_relationships";
import { AddAccountPartyRow } from "../components/party-involved/AccountPartyRow";

export interface CreateCaseFormInterface {
    stepCount?: number;
    currentStep?: number;
    onStepChange?: (stepIndex: number) => void;
}

/** Legacy loan row used by the Operational/Cyber (old) Party-Involved design. */
export interface LoanProductInfoRow {
    id: string;
    product: string;
    loanReferenceIds: string[];
}

export interface CreateCaseFormPayload {
    source: OptionNameValuePair<string> | undefined;
    referrer_name: string;
    referral_date: string;
    region: OptionNameValuePair<string> | undefined;
    zone: OptionNameValuePair<string> | undefined;
    branch: OptionNameValuePair<string> | undefined;
    forwarding_authority: OptionNameValuePair<string> | undefined;
    investigation_nature: OptionNameValuePair<string>[] | undefined;
    background: string;
    area_of_fraud: OptionNameValuePair<string> | undefined;
    account_type?: OptionNameValuePair<AccountType> | undefined;
    loanDetails?: LoanProductInfoRow[];
    has_employee_involvement?: boolean;
    employee_ids?: string[];
    parties_involved?: PartyInvolved[];
}

export type BasicDetailsState = Pick<CreateCaseFormPayload, "source" | "referrer_name" | "referral_date" | "region" | "zone" | "branch" | "forwarding_authority">;
export type BackgroundState = Pick<CreateCaseFormPayload, "investigation_nature" | "background">;
export type PartyInvolvedState = Pick<CreateCaseFormPayload, "area_of_fraud" | "account_type" | "has_employee_involvement" | "employee_ids" | "parties_involved" | "loanDetails">;

export interface PartyInvolvedData {
    loanAccounts: LoanAccount[];
    primaryParties: PrimaryParty[];
}

export interface AccountPartySubmitData {
    accounts: Account[];
    primaryParties: AddAccountPartyRow[];
}

export const validateField = (fieldName: string, value: any): string | null => {
    if (value === undefined || value === null || (typeof value === "string" && value.trim() === "")) {
        const sanitizedFieldName = fieldName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        return `${sanitizedFieldName} is required`;
    }
    return null;
};

// --- Per-step validators (self-contained steps) ---


/** Legacy (Operational / Cyber, and flag-OFF Credit) Party-Involved validation. */


// --- Legacy row validators (Operational / Cyber) ---

// --- Request builders ---
const toCreditAccount = (a: LoanAccount): CreditAccount => ({
    account_number: a.accountNumber,
    ext_account_number: a.extAccountNumber.trim(),
    account_name: a.accountName.trim(),
    product: a.product as CreditAccountProduct,
    credit_facility: a.creditFacility as CreditFacility,
});

const buildRelationRelationships = (
    r: AccountRelations
): StakeholderRelationshipFields[] => {
    if (!r.related_as) return [];
    if (LOAN_ACCOUNT_ROLES.includes(r.related_as) && r.account_numbers.length > 0) {
        return r.account_numbers.map((account_number) => ({
            entity_type: EntityType.LOAN_ACCOUNT,
            entity_id: account_number,
            related_as: r.related_as ?? RelatedAs.OTHER,
        }));
    }
    return [{ entity_type: EntityType.CASE, entity_id: "", related_as: r.related_as }];
};

export const buildStakeholders = (
    parties: PrimaryParty[]
): CreateCaseStakeholderRequest[] =>
    parties
        .filter((p) => p.name.trim() !== "")
        .map((p) => {
            const employeeId = p.employee_details?.employee_id?.trim();
            return {
                stakeholder: { name: p.name.trim(), is_potential_perpetrator: true },
                relationships: p.account_relationships.flatMap(buildRelationRelationships),
                ...(employeeId ? { employee_details: { employee_id: employeeId } } : {}),
            };
        });

export const buildStakeholdersFromParties = (
    parties: AddAccountPartyRow[],
): CreateCaseStakeholderRequest[] =>
    parties
        .filter((p) => p.stakeholder.name.trim() !== "")
        .map((p) => {
            const employeeId = p.employee_details?.employee_id?.trim();
            const relationships: StakeholderRelationshipFields[] = p.relationshipGroups.flatMap((group) => (
                group.accountNumbers.map((an) => ({
                    entity_type: EntityType.ACCOUNT,
                    entity_id: an,
                    related_as: group.relatedAs ?? RelatedAs.OTHER,
                }))
            ))
            return {
                stakeholder: {
                    name: p.stakeholder.name.trim(),
                    is_potential_perpetrator: p.stakeholder.is_potential_perpetrator ?? true,
                },
                relationships: relationships,
                ...(employeeId ? { employee_details: { employee_id: employeeId } } : {}),
            };
        });

export const getCreateCaseRequest = (
    payload: CreateCaseFormPayload,
    pi?: PartyInvolvedData,
    accountParty?: AccountPartySubmitData,
): CreateCaseRequest => {
    const isCredit = payload.area_of_fraud?.value === AreaOfFraud.CREDIT;
    const isCyber = payload.area_of_fraud?.value === AreaOfFraud.CYBER;
    const isOperational = payload.area_of_fraud?.value === AreaOfFraud.OPERATIONAL;

    const commonCase = {
        uin: generateCaseUIN(payload.region?.value),
        loan_related: false,
        region: payload.region?.value || "",
        branch: payload.branch?.value || "",
        zone: payload.zone?.value || "",
        referrer: payload.referrer_name || "",
        referral_date: payload.referral_date,
        investigation_nature: payload.investigation_nature?.map((n) => n.value) || [],
        area_of_fraud: payload.area_of_fraud?.value as AreaOfFraud | undefined,
        customer: "",
        primary_party: "",
        background: payload.background || "",
        exposure_currency: undefined,
        exposure: undefined,
        source: payload.source?.value || "",
        party_type: "",
    };

    // Credit (V5): loan accounts + stakeholders carry the data. Only taken when
    // the parent supplies the Credit state (`pi`), i.e. the revamp flag is ON.
    if (isCredit && pi) {
        return {
            case: {
                ...commonCase,
                account_type: AccountType.OTHERS,
                employee_involvement: false,
                employee_ids: [],
                loan_product_info: [],
            },
            parties_involved: [],
            credit_accounts: pi.loanAccounts.map(toCreditAccount),
            stakeholders: buildStakeholders(pi.primaryParties),
            forwarding_auth_id: payload.forwarding_authority?.value ?? "",
        };
    }

    // Cyber / Operational Accounts path.
    if ((isCyber || isOperational) && accountParty) {
        return {
            case: {
                ...commonCase,
                account_type: AccountType.OTHERS,
                employee_involvement: false,
                employee_ids: [],
                loan_product_info: [],
            },
            parties_involved: [],
            accounts: accountParty.accounts,
            stakeholders: buildStakeholdersFromParties(accountParty.primaryParties),
            forwarding_auth_id: payload.forwarding_authority?.value ?? "",
        };
    }

    // Operational / Cyber (legacy design), and Credit with the flag OFF — unchanged.
    const loan_product_info = (payload.loanDetails ?? []).flatMap((row) =>
        row.loanReferenceIds.map((refId) => ({ loan_reference_id: refId, product: row.product }))
    );
    const employee_ids = payload.has_employee_involvement
        ? (payload.employee_ids?.filter((v) => v !== "").map((v) => v.trim()) ?? [])
        : [];

    return {
        case: {
            ...commonCase,
            account_type: payload.account_type?.value ?? AccountType.OTHERS,
            employee_involvement: !!payload.has_employee_involvement,
            employee_ids,
            loan_product_info,
        },
        parties_involved: payload.parties_involved ?? [],
        forwarding_auth_id: payload.forwarding_authority?.value ?? "",
    };
};
